import Phaser from 'phaser';
import { coinLabel } from '../../config/coinLabel';
import { LAST_LEVEL_ID, WIN_GATE_LEVEL_IDS } from '../../config/levels';
import {
  BAG_COST,
  BAG_PREP_MS,
  GOLDEN_BAG_COST,
  GOLDEN_BAG_ODDS,
  GOLDEN_BAG_PREP_MS,
  TAKEOUT_BAG_ODDS,
  RARITY_COLORS,
  type BagSlot,
  formatCountdown,
  oddsLabel,
  remainingMs,
} from '../../config/restaurant';
import { TOWER_NAMES } from '../../config/displayNames';
import { ProgressService } from '../../services/progressService';
import { saveStorage } from '../saveStorage';
import { recordStat } from '../quests';
import { type BagOpenOptions, openGoldenBag, openTakeoutBag } from '../bagOpening';
import { spinCoin } from '../coin';
import { getAudioService } from '../audio/AudioService';
import { addMuteButton } from '../audio/audioControls';
import { addFullscreenButton } from '../screenControls';
import { installDebugReturn } from '../debugMode';

const GOLD = 0xf5c518;
const GOLD_HEX = '#f5c518';
const GREEN = 0x2f9e3f;
const GREEN_LIT = 0x3fc253;

/** Where the action control (ORDER / timer / OPEN+STORE) is centred. */
const ACTION_Y = 452;

/** The page arrows: one at each side, level with the bag. */
const ARROW_Y = 288;
const ARROW_LEFT_X = 150;
const ARROW_RIGHT_X = 1130;

/**
 * One Restaurant page — everything that differs between the Normal and the
 * Golden Takeout Bag. The scene draws whichever page is showing from this and
 * knows nothing else about either bag.
 */
interface BagPage {
  /** Prefix for every named control on the page (the e2e targets these). */
  readonly id: 'restaurant' | 'restaurant-golden';
  readonly title: string;
  readonly cost: number;
  readonly art: string;
  readonly odds: readonly BagSlot[];
  readonly prepMs: number;
  readonly orderedAt: (p: ProgressService) => number | null;
  readonly order: (p: ProgressService, now: number) => boolean;
  readonly clearOrder: (p: ProgressService) => void;
  readonly store: (p: ProgressService) => boolean;
  readonly open: (scene: Phaser.Scene, opts: BagOpenOptions) => void;
}

export const RESTAURANT_PAGES: readonly BagPage[] = [
  {
    id: 'restaurant',
    title: 'NORMAL TAKEOUT BAG',
    cost: BAG_COST,
    art: 'takeout-normal',
    odds: TAKEOUT_BAG_ODDS,
    prepMs: BAG_PREP_MS,
    orderedAt: (p) => p.bagOrderedAt(),
    order: (p, now) => p.orderBag(BAG_COST, now),
    clearOrder: (p) => p.clearBagOrder(),
    store: (p) => p.storeBag(),
    open: (scene, opts) => openTakeoutBag(scene, opts),
  },
  {
    id: 'restaurant-golden',
    title: 'GOLDEN TAKEOUT BAG',
    cost: GOLDEN_BAG_COST,
    art: 'takeout-golden',
    odds: GOLDEN_BAG_ODDS,
    prepMs: GOLDEN_BAG_PREP_MS,
    orderedAt: (p) => p.goldenBagOrderedAt(),
    order: (p, now) => p.orderGoldenBag(GOLDEN_BAG_COST, now),
    clearOrder: (p) => p.clearGoldenBagOrder(),
    store: (p) => p.storeGoldenBag(),
    open: (scene, opts) => openGoldenBag(scene, opts),
  },
];

/**
 * RestaurantScene — order a takeout bag, wait for it to cook, open it.
 *
 * Two bags, one per PAGE (spec 2026-09-26): the Normal Takeout Bag (100 coins,
 * ten minutes) and the Golden Takeout Bag (500 coins, five minutes). The
 * ◀ ▶ arrows at each side flip between them; each page has its own ORDER,
 * countdown and OPEN / STORE, and the two bags cook independently.
 *
 * The Restaurant replaced the Shop's tower rows as the way towers are obtained
 * (see config/restaurant.ts for the drop tables and config/unlocks.ts for why
 * the retired shop roster still exists). Reached from the Shop's RESTAURANT
 * pill; BACK returns there.
 *
 * The cook times are wall-clock, stored as the order's timestamp, so they run
 * while the game is closed — the screen only ever asks "how much is left?", it
 * never counts anything down itself.
 */
export class RestaurantScene extends Phaser.Scene {
  private progress!: ProgressService;
  private balanceText!: Phaser.GameObjects.Text;
  /** Which bag is showing: an index into RESTAURANT_PAGES. */
  private pageIndex = 0;
  /** Everything that belongs to the showing page — rebuilt on a flip. */
  private pageLayer?: Phaser.GameObjects.Container;
  /** The ORDER button / countdown / OPEN+STORE pair — rebuilt on every change. */
  private actionLayer?: Phaser.GameObjects.Container;
  /** Live countdown label while a bag is cooking (absent otherwise). */
  private countdown?: Phaser.GameObjects.Text;
  /** Transient footer hint (e.g. can't afford). */
  private hint?: Phaser.GameObjects.Text;
  /** True while the roulette/reveal is up — the action buttons ignore clicks. */
  private busy = false;

  constructor() {
    super({ key: 'RestaurantScene' });
  }

  /** `page` opens straight onto a bag (1 = Golden) — for `?debug` and the e2e. */
  init(data?: { page?: number }): void {
    const p = data?.page ?? 0;
    this.pageIndex = p >= 0 && p < RESTAURANT_PAGES.length ? p : 0;
  }

  private get page(): BagPage {
    return RESTAURANT_PAGES[this.pageIndex];
  }

  create(): void {
    const cx = 640;
    this.progress = new ProgressService(saveStorage(), LAST_LEVEL_ID, WIN_GATE_LEVEL_IDS);
    this.pageLayer = undefined;
    this.actionLayer = undefined;
    this.countdown = undefined;
    this.hint = undefined;
    this.busy = false;

    this.add.image(640, 360, 'bg-shop').setDisplaySize(1280, 720);
    this.add.rectangle(640, 360, 1280, 720, 0x000000, 0.55);

    // ── Header ──────────────────────────────────────────────────────────────
    this.add
      .text(cx, 44, 'RESTAURANT', { fontSize: '38px', color: GOLD_HEX, fontStyle: 'bold' })
      .setOrigin(0.5);
    this.add
      .text(cx, 80, 'ORDER A TAKEOUT BAG · EVERY BAG HOLDS A TOWER', {
        fontSize: '15px',
        color: '#c8d2d8',
      })
      .setOrigin(0.5)
      .setShadow(0, 2, '#000000', 4);

    const backBtn = this.add
      .text(80, 50, '← BACK', {
        fontSize: '22px',
        color: '#cfe0bd',
        backgroundColor: '#15131f',
        padding: { x: 16, y: 12 },
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
    backBtn.on('pointerover', () => backBtn.setColor(GOLD_HEX));
    backBtn.on('pointerout', () => backBtn.setColor('#cfe0bd'));
    backBtn.on('pointerup', () => {
      if (this.busy) return;
      this.scene.start('ShopScene');
    });

    const coinIcon = this.add.image(960, 50, 'coin').setDisplaySize(34, 34).setDepth(5);
    spinCoin(this, coinIcon);
    this.balanceText = this.add
      .text(982, 50, coinLabel(this.progress.coins()), {
        fontSize: '22px',
        color: GOLD_HEX,
        fontStyle: 'bold',
      })
      .setOrigin(0, 0.5)
      .setDepth(5);

    addMuteButton(this, 1245, 50, getAudioService(this));
    addFullscreenButton(this, 1100, 50);

    // ── Page arrows, one each side ───────────────────────────────────────────
    this.buildArrow(ARROW_LEFT_X, -1, 'restaurant-prev');
    this.buildArrow(ARROW_RIGHT_X, 1, 'restaurant-next');

    this.renderPage();

    installDebugReturn(this); // Esc → DemoScene when launched via ?debug
  }

  /**
   * A big ◀ / ▶ button. With two bags each arrow simply shows the other one;
   * the pages wrap, so a third bag would need no new wiring.
   */
  private buildArrow(x: number, dir: -1 | 1, name: string): void {
    const box = this.add
      .rectangle(x, ARROW_Y, 76, 96, 0x15131f, 0.9)
      .setStrokeStyle(3, GOLD, 0.85)
      .setInteractive({ useHandCursor: true })
      .setName(name)
      .setDepth(6);
    const tri = this.add.graphics().setDepth(7);
    const w = 20;
    const h = 26;
    tri.fillStyle(GOLD, 1);
    tri.fillTriangle(x - dir * w, ARROW_Y - h, x - dir * w, ARROW_Y + h, x + dir * w, ARROW_Y);
    box.on('pointerover', () => box.setFillStyle(0x2a2540, 0.95));
    box.on('pointerout', () => box.setFillStyle(0x15131f, 0.9));
    box.on('pointerup', () => {
      if (this.busy) return;
      const n = RESTAURANT_PAGES.length;
      this.pageIndex = (this.pageIndex + dir + n) % n;
      this.renderPage();
    });
  }

  /** Draw the showing bag: name + price, art, the odds list, the control. */
  private renderPage(): void {
    this.pageLayer?.destroy();
    this.hint?.destroy();
    this.hint = undefined;
    const layer = this.add.container(0, 0);
    this.pageLayer = layer;
    const page = this.page;
    const cx = 640;

    // ── The bag: name + price above, art below ───────────────────────────────
    const nameText = this.add
      .text(cx - 40, 136, page.title, {
        fontSize: '26px',
        color: page.id === 'restaurant-golden' ? GOLD_HEX : '#ffffff',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setName(`${page.id}-title`);
    layer.add(nameText);
    // Price sits immediately to the right of the name, as one line.
    const priceX = nameText.x + nameText.width / 2 + 22;
    layer.add(this.add.image(priceX, 136, 'coin').setDisplaySize(26, 26));
    layer.add(
      this.add
        .text(priceX + 18, 136, `${page.cost}`, {
          fontSize: '26px',
          color: GOLD_HEX,
          fontStyle: 'bold',
        })
        .setOrigin(0, 0.5),
    );

    if (page.id === 'restaurant-golden') {
      // A soft gold halo, so the Golden bag reads as the special one.
      const halo = this.add.circle(cx, 288, 118, GOLD, 0.16);
      layer.add(halo);
      this.tweens.add({
        targets: halo,
        alpha: { from: 0.08, to: 0.24 },
        duration: 900,
        yoyo: true,
        repeat: -1,
      });
    }
    layer.add(this.add.image(cx, 288, page.art).setDisplaySize(210, 210));

    // ── Odds list, under the action control ──────────────────────────────────
    layer.add(
      this.add
        .text(cx, 540, 'WHAT’S INSIDE', { fontSize: '16px', color: '#cfe0bd', fontStyle: 'bold' })
        .setOrigin(0.5),
    );
    // Six rows (the Golden bag since 2026-09-29) close up a little so the last
    // one clears the bottom edge; five keep the original 26 px.
    const rowGap = page.odds.length > 5 ? 23 : 26;
    page.odds.forEach((slot, i) => {
      const y = 572 + i * rowGap;
      layer.add(
        this.add
          .text(cx - 150, y, TOWER_NAMES[slot.tower], {
            fontSize: '16px',
            color: Phaser.Display.Color.IntegerToColor(RARITY_COLORS[slot.rarity]).rgba,
            fontStyle: 'bold',
          })
          .setOrigin(0, 0.5),
      );
      layer.add(
        this.add
          .text(cx + 150, y, oddsLabel(slot), { fontSize: '16px', color: '#ffffff' })
          .setOrigin(1, 0.5),
      );
    });

    this.renderAction();
  }

  /**
   * Refresh the countdown once a second. Reading the remaining time from the
   * stored timestamp (rather than decrementing a number) is what makes an
   * offline wait behave the same as one spent staring at it.
   */
  override update(): void {
    if (!this.countdown) return;
    const orderedAt = this.page.orderedAt(this.progress);
    if (orderedAt == null) return;
    const left = remainingMs(orderedAt, Date.now(), this.page.prepMs);
    this.countdown.setText(formatCountdown(left));
    if (left <= 0) this.renderAction(); // cooked — swap in OPEN / STORE
  }

  /** (Re)build whichever control the showing bag's current state calls for. */
  private renderAction(): void {
    this.actionLayer?.destroy();
    this.countdown = undefined;
    const layer = this.add.container(0, 0).setDepth(6);
    this.actionLayer = layer;

    const orderedAt = this.page.orderedAt(this.progress);
    if (orderedAt == null) {
      this.buildOrderButton(layer);
      return;
    }
    const left = remainingMs(orderedAt, Date.now(), this.page.prepMs);
    if (left > 0) this.buildTimer(layer, left);
    else this.buildOpenStore(layer);
  }

  /** The green ORDER button (dimmed, but still tappable, when unaffordable). */
  private buildOrderButton(layer: Phaser.GameObjects.Container): void {
    const page = this.page;
    const affordable = this.progress.coins() >= page.cost;
    const box = this.add
      .rectangle(640, ACTION_Y, 340, 62, affordable ? GREEN : 0x2a3a2a)
      .setStrokeStyle(3, affordable ? 0x8be04a : 0x4a5a4a)
      .setInteractive({ useHandCursor: affordable })
      .setName(`${page.id}-order`);
    layer.add(box);
    const label = this.add
      .text(640 - 14, ACTION_Y, `ORDER FOR ${page.cost}`, {
        fontSize: '24px',
        color: affordable ? '#ffffff' : '#7f8c8d',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    layer.add(label);
    layer.add(
      this.add
        .image(640 + label.width / 2 - 2, ACTION_Y, 'coin')
        .setDisplaySize(26, 26)
        .setAlpha(affordable ? 1 : 0.4),
    );

    if (affordable) {
      box.on('pointerover', () => box.setFillStyle(GREEN_LIT));
      box.on('pointerout', () => box.setFillStyle(GREEN));
    }
    box.on('pointerup', () => {
      if (this.busy) return;
      if (!affordable) {
        this.flashHint('Not enough coins — win episodes to earn more.');
        return;
      }
      if (page.order(this.progress, Date.now())) {
        recordStat('obtains'); // Quests: a takeout bag is an item
        this.balanceText.setText(coinLabel(this.progress.coins()));
        this.renderAction();
      }
    });
  }

  /** The countdown that stands in for the ORDER button while cooking. */
  private buildTimer(layer: Phaser.GameObjects.Container, remaining: number): void {
    layer.add(
      this.add
        .rectangle(640, ACTION_Y, 340, 62, 0x15131f, 0.95)
        .setStrokeStyle(3, GOLD, 0.8)
        .setName(`${this.page.id}-timer`),
    );
    layer.add(
      this.add
        .text(640, ACTION_Y - 15, 'COOKING…', { fontSize: '14px', color: '#9aa6ad' })
        .setOrigin(0.5),
    );
    this.countdown = this.add
      .text(640, ACTION_Y + 10, formatCountdown(remaining), {
        fontSize: '30px',
        color: GOLD_HEX,
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    layer.add(this.countdown);
  }

  /** OPEN and STORE, side by side, once the bag is ready. */
  private buildOpenStore(layer: Phaser.GameObjects.Container): void {
    const page = this.page;
    const mk = (
      x: number,
      text: string,
      fill: number,
      stroke: number,
      name: string,
      onClick: () => void,
    ) => {
      const box = this.add
        .rectangle(x, ACTION_Y, 210, 62, fill)
        .setStrokeStyle(3, stroke)
        .setInteractive({ useHandCursor: true })
        .setName(name);
      layer.add(box);
      layer.add(
        this.add
          .text(x, ACTION_Y, text, { fontSize: '24px', color: '#ffffff', fontStyle: 'bold' })
          .setOrigin(0.5),
      );
      box.on('pointerup', () => {
        if (this.busy) return;
        onClick();
      });
    };

    layer.add(
      this.add
        .text(640, ACTION_Y - 52, 'YOUR BAG IS READY!', {
          fontSize: '18px',
          color: '#8be04a',
          fontStyle: 'bold',
        })
        .setOrigin(0.5),
    );

    mk(640 - 116, 'OPEN', GREEN, 0x8be04a, `${page.id}-open`, () => {
      // Consume the order BEFORE rolling: the bag is spent the moment it is
      // opened, so a mid-animation reload can never reopen the same bag.
      page.clearOrder(this.progress);
      this.busy = true;
      this.renderAction();
      page.open(this, {
        progress: this.progress,
        onDone: () => {
          this.busy = false;
          this.balanceText.setText(coinLabel(this.progress.coins()));
          this.renderAction();
        },
      });
    });
    mk(640 + 116, 'STORE', 0x3f4a6a, 0x8fa2d8, `${page.id}-store`, () => {
      page.store(this.progress);
      this.renderAction();
      this.flashHint('Stored — find it under STORAGE on the episode screen.');
    });
  }

  /** Transient footer hint. */
  private flashHint(msg: string): void {
    this.hint?.destroy();
    this.hint = this.add
      .text(640, 700, msg, { fontSize: '15px', color: '#e8c07a', fontStyle: 'italic' })
      .setOrigin(0.5)
      .setDepth(50);
    this.time.delayedCall(2200, () => {
      this.hint?.destroy();
      this.hint = undefined;
    });
  }

  /** Full prep time, re-exported for the tests/e2e that seed a finished bag. */
  static readonly PREP_MS = BAG_PREP_MS;
  /** The Golden bag's prep time, for the same tests. */
  static readonly GOLDEN_PREP_MS = GOLDEN_BAG_PREP_MS;
}
